import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Star, Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { formatPrice } from '../lib/format';
import { isSaleActive } from '../lib/sale';
import { SaleCountdown } from './SaleCountdown';
import ImageWithRetry from './ImageWithRetry';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to proxy images from kalyantop.com.ua
const getProxiedImage = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.includes('kalyantop.com.ua')) {
    return `${API}/image-proxy?url=${encodeURIComponent(imageUrl)}`;
  }
  return imageUrl;
};

const ProductCard = ({ product }) => {
  const { t, getLocalizedText, language } = useLanguage();
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const navigate = useNavigate();
  const inWishlist = isInWishlist(product.id);
  const onSale = isSaleActive(product);

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (!product.inStock) return;
    // Use proxied image for cart
    const cartProduct = {
      ...product,
      image: getProxiedImage(product.image)
    };
    addToCart(cartProduct);
  };

  const handleWishlistToggle = (e) => {
    e.stopPropagation();
    toggleWishlist(product);
  };

  const handleClick = () => {
    navigate(`/product/${product.id}`);
  };

  return (
    <div
      onClick={handleClick}
      className="group relative bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all duration-300 cursor-pointer"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className={`relative overflow-hidden bg-zinc-950 ${product.category === 'set' ? 'aspect-[3/4]' : 'aspect-square'}`}>
        <ImageWithRetry
          src={getProxiedImage(product.image)}
          alt={getLocalizedText(product.name)}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          fallbackText={product.brand || 'Product'}
          fallbackSize="228x228"
        />
        {/* Wishlist Button */}
        <button
          onClick={handleWishlistToggle}
          className={`absolute top-3 right-3 z-10 p-2 rounded-full transition-all duration-200 ${
            inWishlist 
              ? 'bg-red-500 text-white' 
              : 'bg-black/50 text-white hover:bg-black/70'
          }`}
          data-testid={`wishlist-${product.id}`}
        >
          <Heart className={`h-4 w-4 ${inWishlist ? 'fill-current' : ''}`} />
        </button>
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3 pointer-events-none">
          {product.inStock ? (
            <Button
              size="sm"
              className="bg-white text-black hover:bg-zinc-200 pointer-events-auto"
              onClick={handleAddToCart}
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              {t.products.addToCart}
            </Button>
          ) : (
            <span className="bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-md">
              {t.products.outOfStock}
            </span>
          )}
        </div>
        {/* Top-left badges */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
          {product.isBestseller && (
            <Badge className="bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold shadow-md" data-testid={`top-badge-${product.id}`}>
              TOP
            </Badge>
          )}
          {!product.inStock && (
            <Badge className="bg-red-600 text-white">
              {t.products.outOfStock}
            </Badge>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Brand */}
        {product.brand && (
          <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
            {product.brand}
          </span>
        )}
        
        {/* Name */}
        <h3 className="mt-1 font-medium text-white text-sm line-clamp-2 min-h-[2.5rem] group-hover:text-zinc-300 transition-colors">
          {getLocalizedText(product.name)}
        </h3>

        {/* Rating */}
        {product.rating && (
          <div className="flex items-center gap-1 mt-2">
            <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
            <span className="text-xs text-zinc-400">{product.rating}</span>
          </div>
        )}

        {/* Price */}
        <div className="mt-3">
          {onSale ? (
            <div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-lg font-bold text-red-500" data-testid={`sale-price-${product.id}`}>
                  {formatPrice(product.salePrice)} <span className="text-sm text-red-400/80">HUF</span>
                </span>
                <span className="text-sm text-zinc-500 line-through">
                  {formatPrice(product.price)}
                </span>
              </div>
              {product.saleEndDate && (
                <SaleCountdown saleEndDate={product.saleEndDate} language={language} className="mt-1" />
              )}
            </div>
          ) : (
            <span className="text-lg font-bold text-white">
              {formatPrice(product.price)} <span className="text-sm text-zinc-500">HUF</span>
            </span>
          )}
        </div>

        {/* Description */}
        {product.description && getLocalizedText(product.description) && (
          <p className="mt-3 text-xs text-zinc-400 line-clamp-2">
            {getLocalizedText(product.description)}
          </p>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
